import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Container,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Store as StoreIcon,
  Login as LoginIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { gradients } from "../utils/theme";
import MallInHandLogo from "./MallInHandLogo";

interface PublicNavbarProps {
  transparent?: boolean;
}

/**
 * Reusable NavLinkButton Component for Desktop Navigation
 */
interface NavLinkButtonProps {
  label: string;
  sectionId: string;
  isScrolledOrNotTransparent: boolean;
  onClick: (sectionId: string) => void;
}

const NavLinkButton: React.FC<NavLinkButtonProps> = ({
  label,
  sectionId,
  isScrolledOrNotTransparent,
  onClick,
}) => (
  <Button
    onClick={() => onClick(sectionId)}
    sx={{
      color: isScrolledOrNotTransparent ? "text.primary" : "white",
      fontWeight: 500,
      fontSize: "0.938rem",
      textTransform: "none",
      px: 1.5,
      "&:hover": {
        bgcolor: isScrolledOrNotTransparent
          ? "rgba(26,35,126,0.08)"
          : "rgba(255,255,255,0.1)",
      },
    }}
  >
    {label}
  </Button>
);

/**
 * Reusable DrawerMenuItem Component for Mobile Navigation
 */
interface DrawerMenuItemProps {
  label: string;
  sectionId: string;
  onClick: (sectionId: string) => void;
}

const DrawerMenuItem: React.FC<DrawerMenuItemProps> = ({
  label,
  sectionId,
  onClick,
}) => (
  <ListItem disablePadding sx={{ mb: 1 }}>
    <ListItemButton
      onClick={() => onClick(sectionId)}
      sx={{
        borderRadius: 2,
        py: 1.5,
        "&:hover": {
          bgcolor: "rgba(255, 255, 255, 0.1)",
        },
      }}
    >
      <ListItemText
        primary={label}
        sx={{
          "& .MuiListItemText-primary": {
            color: "white",
            fontWeight: 500,
            fontSize: "1.1rem",
          },
        }}
      />
    </ListItemButton>
  </ListItem>
);

/**
 * DesktopCTAButton - Reusable component for desktop CTA buttons with motion effects
 */
interface DesktopCTAButtonProps {
  label: string;
  icon: React.ReactNode;
  variant: "outlined" | "contained";
  gradient?: string;
  isScrolledOrNotTransparent: boolean;
  onClick: () => void;
  delay: number;
}

const DesktopCTAButton: React.FC<DesktopCTAButtonProps> = ({
  label,
  icon,
  variant,
  gradient,
  isScrolledOrNotTransparent,
  onClick,
  delay,
}) => (
  <motion.div
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
  >
    <Button
      variant={variant}
      startIcon={icon}
      onClick={onClick}
      sx={{
        ...(variant === "outlined"
          ? {
              color: isScrolledOrNotTransparent ? "primary.main" : "white",
              borderColor: isScrolledOrNotTransparent
                ? "primary.main"
                : "rgba(255,255,255,0.5)",
              "&:hover": {
                borderColor: isScrolledOrNotTransparent
                  ? "primary.dark"
                  : "white",
                bgcolor: isScrolledOrNotTransparent
                  ? "rgba(26,35,126,0.08)"
                  : "rgba(255,255,255,0.1)",
              },
            }
          : {
              background: gradient,
              color: "white",
              boxShadow: `0 4px 16px ${
                gradient === gradients.gold
                  ? "rgba(212, 175, 55, 0.4)"
                  : "rgba(26, 35, 126, 0.4)"
              }`,
              "&:hover": {
                background:
                  gradient === gradients.gold
                    ? "linear-gradient(135deg, #b8942d 0%, #d4a900 100%)"
                    : "linear-gradient(135deg, #0d1b5e 0%, #1e2875 100%)",
                boxShadow: `0 6px 24px ${
                  gradient === gradients.gold
                    ? "rgba(212, 175, 55, 0.6)"
                    : "rgba(26, 35, 126, 0.6)"
                }`,
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
            }),
        px: 3,
        py: 1.25,
        fontSize: "1rem",
        fontWeight: 600,
        textTransform: "none",
        borderRadius: 2,
      }}
    >
      {label}
    </Button>
  </motion.div>
);

/**
 * MobileCTAButton - Reusable component for mobile CTA buttons
 */
interface MobileCTAButtonProps {
  label: string;
  icon: React.ReactNode;
  gradient: string;
  onClick: () => void;
}

const MobileCTAButton: React.FC<MobileCTAButtonProps> = ({
  label,
  icon,
  gradient,
  onClick,
}) => (
  <Button
    variant="contained"
    size="small"
    startIcon={icon}
    onClick={onClick}
    sx={{
      background: gradient,
      color: "white",
      px: 1.5,
      py: 0.75,
      fontSize: "0.813rem",
      fontWeight: 600,
      textTransform: "none",
      borderRadius: 1.5,
      boxShadow: `0 2px 8px ${
        gradient === gradients.gold
          ? "rgba(212, 175, 55, 0.4)"
          : "rgba(26, 35, 126, 0.4)"
      }`,
      "&:hover": {
        background:
          gradient === gradients.gold
            ? "linear-gradient(135deg, #b8942d 0%, #d4a900 100%)"
            : "linear-gradient(135deg, #0d1b5e 0%, #1e2875 100%)",
        boxShadow: `0 4px 12px ${
          gradient === gradients.gold
            ? "rgba(212, 175, 55, 0.6)"
            : "rgba(26, 35, 126, 0.6)"
        }`,
      },
    }}
  >
    {label}
  </Button>
);

const PublicNavbar: React.FC<PublicNavbarProps> = ({ transparent = false }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Detect scroll for transparent navbar + auto-hide on scroll down
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Handle transparent/solid background
      if (transparent) {
        setScrolled(currentScrollY > 50);
      }

      // Auto-hide logic: hide on scroll down, show on scroll up
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down & past 100px - hide navbar
        setHidden(true);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up - show navbar
        setHidden(false);
      }

      // Always show at very top
      if (currentScrollY < 50) {
        setHidden(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [transparent, lastScrollY]);

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setMobileMenuOpen(false);
  };

  // Determine if navbar should be solid (not transparent)
  const isScrolledOrNotTransparent = scrolled || !transparent;

  return (
    <>
      <AppBar
        position="fixed"
        elevation={isScrolledOrNotTransparent ? 2 : 0}
        sx={{
          background: isScrolledOrNotTransparent
            ? "rgba(255, 255, 255, 0.98)"
            : "transparent",
          backdropFilter: isScrolledOrNotTransparent ? "blur(10px)" : "none",
          borderBottom: isScrolledOrNotTransparent
            ? "1px solid rgba(26, 35, 126, 0.1)"
            : "none",
          transform: hidden ? "translateY(-100%)" : "translateY(0)",
          transition: "all 0.3s ease",
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            sx={{
              justifyContent: "space-between",
              py: { xs: 0.75, md: 1 },
              px: { xs: 0, sm: 2 },
              minHeight: { xs: 56, md: 64 },
            }}
          >
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Box
                onClick={() => navigate("/")}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  "&:hover": {
                    opacity: 0.9,
                  },
                }}
              >
                <MallInHandLogo
                  variant="full"
                  width={{ xs: 160, sm: 200, md: 220, lg: 240 }}
                  color={isScrolledOrNotTransparent ? "gradient" : "white"}
                />
              </Box>
            </motion.div>

            {/* Desktop Navigation */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                gap: 3,
              }}
            >
              {/* Navigation Links - Scroll to Sections */}
              <Box sx={{ display: "flex", gap: 3, mr: 2 }}>
                <NavLinkButton
                  label="Features"
                  sectionId="features"
                  isScrolledOrNotTransparent={isScrolledOrNotTransparent}
                  onClick={scrollToSection}
                />
                <NavLinkButton
                  label="How It Works"
                  sectionId="how-it-works"
                  isScrolledOrNotTransparent={isScrolledOrNotTransparent}
                  onClick={scrollToSection}
                />
                <NavLinkButton
                  label="Testimonials"
                  sectionId="user-testimonials"
                  isScrolledOrNotTransparent={isScrolledOrNotTransparent}
                  onClick={scrollToSection}
                />
                <NavLinkButton
                  label="Contact Us"
                  sectionId="contact"
                  isScrolledOrNotTransparent={isScrolledOrNotTransparent}
                  onClick={scrollToSection}
                />
              </Box>

              {/* CTAs - Explore & Login */}
              <Box sx={{ display: "flex", gap: 2 }}>
                <DesktopCTAButton
                  label="Explore Malls"
                  icon={<StoreIcon />}
                  variant="outlined"
                  isScrolledOrNotTransparent={isScrolledOrNotTransparent}
                  onClick={() => handleNavigate("/explore")}
                  delay={0.15}
                />
                <DesktopCTAButton
                  label="Login"
                  icon={<LoginIcon />}
                  variant="contained"
                  gradient={gradients.navyLux}
                  isScrolledOrNotTransparent={isScrolledOrNotTransparent}
                  onClick={() => handleNavigate("/login")}
                  delay={0.2}
                />
              </Box>
            </Box>

            {/* Mobile Navigation - Explore Button + Burger Menu */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                alignItems: "center",
                gap: 1.5,
              }}
            >
              {/* Explore Malls Button */}
              <MobileCTAButton
                label="Explore"
                icon={<StoreIcon sx={{ fontSize: 16 }} />}
                gradient={gradients.navyLux}
                onClick={() => handleNavigate("/explore")}
              />

              {/* Burger Menu Icon */}
              <IconButton
                onClick={handleMobileMenuToggle}
                sx={{
                  color: isScrolledOrNotTransparent ? "primary.main" : "white",
                  bgcolor: isScrolledOrNotTransparent
                    ? "rgba(26, 35, 126, 0.08)"
                    : "rgba(255, 255, 255, 0.15)",
                  "&:hover": {
                    bgcolor: isScrolledOrNotTransparent
                      ? "rgba(26, 35, 126, 0.15)"
                      : "rgba(255, 255, 255, 0.25)",
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer Menu */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        sx={{
          zIndex: 3000, // Above MallHub mockup (z-index: 2000)
          "& .MuiDrawer-paper": {
            width: { xs: "75%", sm: 320 },
            background: gradients.navyLux,
          },
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
            {/* Close Button */}
            <IconButton
              onClick={handleMobileMenuToggle}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Menu Items - Scroll to Sections */}
          <List sx={{ px: 2 }}>
            <DrawerMenuItem
              label="Features"
              sectionId="features"
              onClick={scrollToSection}
            />
            <DrawerMenuItem
              label="How It Works"
              sectionId="how-it-works"
              onClick={scrollToSection}
            />
            <DrawerMenuItem
              label="WhatsApp Journey"
              sectionId="whatsapp-journey"
              onClick={scrollToSection}
            />
            <DrawerMenuItem
              label="Testimonials"
              sectionId="user-testimonials"
              onClick={scrollToSection}
            />
            <DrawerMenuItem
              label="Contact Us"
              sectionId="contact"
              onClick={scrollToSection}
            />
          </List>

          {/* CTAs at Bottom */}
          <Box
            sx={{
              px: 2,
              mt: "auto",
              mb: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              fullWidth
              startIcon={<StoreIcon />}
              onClick={() => handleNavigate("/explore")}
              sx={{
                background: gradients.navyLux,
                color: "white",
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: 2,
                boxShadow: "0 4px 16px rgba(26, 35, 126, 0.4)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #0d1b5e 0%, #1e2875 100%)",
                  boxShadow: "0 6px 24px rgba(26, 35, 126, 0.6)",
                  transform: "translateY(-2px)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Explore Malls
            </Button>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<LoginIcon />}
              onClick={() => handleNavigate("/login")}
              sx={{
                borderColor: "rgba(255, 255, 255, 0.5)",
                color: "white",
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: 2,
                borderWidth: 2,
                "&:hover": {
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  borderColor: "white",
                  borderWidth: 2,
                },
              }}
            >
              Login
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default PublicNavbar;
